import React, { useState, useRef } from "react";
import dayjs from "dayjs";
import {
    Input,
    Button,
    Space,
    Spin,
    Modal,
    Form,
    Select,
    DatePicker,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import CalendarIcon from "../../assets/img/svg/xCalendaricon.svg?react";
import { getUTCDate } from "../../components/utcDate";
import { SearchOutlined } from "@ant-design/icons";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import "./reports.css";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { buildTimestampedFileName } from "../../components/cstDateFileName";
import { exportToCSV } from "../../components/exportToCsv";
import ReportModal from "./report-modal";


const { TextArea } = Input;


const ActiveQueueExport = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalRow, setTotalRow] = useState(1);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [searchFilters, setsearchFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [startTime, setStartTime] = useState("");
    const [rejectionOptions, setRejectOptions] = useState([]);
    const [reasonLoading, setReasonLoading] = useState(false);
    const { roleUserId } = useAuth();
    const { accessToken } = useAuthToken();
    const [isVisible, setIsVisible] = useState(true);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [startfloatLabel, setStartfloatLabel] = useState('');
    const [endfloatLabel, setEndfloatLabel] = useState('');
    const [errors, setErrors] = useState({});
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalBody, setModalBody] = useState("");
    const searchInput = useRef(null);

    const handleReset = (dataIndex, confirm, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
        });
    };


    const validate = () => {
        const newErrors = {};

        if (!selectedStartDate) {
            newErrors.startdate = "Please select start date.";
        }
        if (!selectedEndDate) {
            newErrors.enddate = "Please select end date.";
        }

        if (selectedStartDate && selectedEndDate && new Date(selectedEndDate) < new Date(selectedStartDate)) {
            newErrors.datevalidate = "End Date cannot be lesser than Start Date";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStartDateChange = (date) => {
        setSelectedStartDate(date);
        if (date !== null) {
            setStartfloatLabel('float-label');
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.startdate;
                delete newErrors.datevalidate;
                return newErrors;
            });
        } else {
            setStartfloatLabel('');
        }
    };

    const disableFutureDates = (current) => {
        const today = new Date();
        return current && current > today;
    };

    const handleEndDateChange = (date) => {
        setSelectedEndDate(date);
        if (date !== null) {
            setEndfloatLabel('float-label');
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.enddate;
                delete newErrors.datevalidate;
                return newErrors;
            });
        } else {
            setEndfloatLabel('');
        }
    };

    const handleCleardate = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setStartfloatLabel('');
        setEndfloatLabel('');
        setTotalRow(0);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));
        setErrors({});
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        Search
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            handleReset(dataIndex, confirm, clearFilters);
                            confirm();
                        }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
        ),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current.select(), 100);
            }
        },
        sorter: true,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
    });

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
    };

    const handleDownloadClick = async () => {
        if (!validate()) {
            focusFirstErrorField();
            return;
        }
        await performDownload();
    };

    const focusFirstErrorField = () => {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
            const element = document.getElementById(firstErrorField);
            if (element) element.focus();
        }
    };

    const performDownload = async () => {
        setLoading(true);
        setError(null);

        const payload = buildPayload();

        try {
            const responseData = await fetchReportData(payload);
            const processedData = processResponseData(responseData);
            if (processedData[0]?.odbcReport?.length === 0) {
                message.error("No data found for selected date range.");
                setPagination((prev) => ({
                    ...prev,
                    current: 1,
                    total: 0,
                }));
                return false;
            }
            const reportRows = processedData[0]?.odbcReport || processedData;
            const reportMeta = reportRows?.[0];
            if (
                reportMeta?.reportDirectDownload &&
                reportMeta?.reportMessage?.trim()
            ) {
                setModalTitle("Report Processing Initiated");
                setModalBody(reportMeta.reportMessage);
                setReportModalVisible(true);
                return;
            } 
            const rowdata = processResponseData(reportRows);
            const fileName = buildTimestampedFileName("BID_ODBC");
            exportToCSV(rowdata, columns, fileName);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const buildPayload = () => {
        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;
        return { "startDate": startdate, "endDate": enddate };
    };

    const fetchReportData = async (payload) => {
        const myHeaders = {
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp(),
            "Content-Type": "application/json",
        };
        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;

        const payload_new = {
            startDate: startdate,
            endDate: enddate,
            reportID: 12,
            userIndex: roleUserId,
            userTimeZone: "string",
            pageNo: 1,
            pageSize: -1,
            search: "null",
            orderByColumn: "string",
            orderBy: "string",
            reportName: "ODBC Report",
        };
        const response = await fetch(CommonObj.Endpoints.GetReportData, {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(payload_new),
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const responseText = await response.text();
        return parseResponseText(responseText);
    };

    const parseResponseText = (responseText) => {
        try {
            const result = JSON.parse(responseText);
            return responseText.includes("No data found") ? [] : result;
        } catch (e) {
            if (responseText.includes("No data found")) {
                return [];
            }
            throw new Error(responseText || "Failed to parse API response.");
        }
    };

    const processResponseData = (result) => {
        const books = Array.isArray(result) ? result : [result];
        return books.map(row => ({
            ...row,
            isbN10: `'${row.isbN10}'`,
            isbN13: `'${row.isbN13}'`,
            isbn: `'${row.isbn}'`,
            createdOn: `'${row.createdOn}'`,
        }));
    };

    const handleManifestReject = async (key) => {
        const commaSeparatedIds = selectedRowKeys.join(",");
        let bids;
        if (commaSeparatedIds) {
            bids = commaSeparatedIds;
        } else {
            bids = selectedRecord.bidNo;
        }

        try {
            const loadingKey = "rejecting";
            message.loading({ content: "Rejecting BID...", key: loadingKey });
            const endUTCTime = getUTCDate();
            const payload = {
                biDs: bids,
                userId: roleUserId,
                startTime: startTime,
                endTime: endUTCTime?.UTCDate,
                rejectionType: key.rejectionType,
                rejectionReason: key.rejectionReason,
            };

            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.RejectManifest, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (response) {
                fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });

                const startUTCTime = getUTCDate();
                setStartTime(startUTCTime?.UTCDate);
                message.success({
                    content: "BID Rejected Successfully!",
                    key: loadingKey,
                    duration: 2,
                });
                handleCancel();
                setSelectedRowKeys([]);
            } else {
                message.error({
                    content: "Failed to Reject BID.",
                    key: loadingKey,
                    duration: 2,
                });
            }
        } catch (error) {
            console.error("API Error:", error);
            message.error({
                content: "An error occurred. Please try again.",
                key: "rejecting",
                duration: 2,
            });
        }
    };

    const columns = [
        {
            title: "BID",
            dataIndex: "bid",
            key: "bid",
            width: 160,
            sorter: (a, b) => a.bid.localeCompare(b.bid),
            ...getColumnSearchProps("bid"),
        },
        {
            title: "ISBN",
            dataIndex: "isbn",
            key: "isbn",
            width: 200,
            sorter: (a, b) => a.isbn.localeCompare(b.isbn),
            ...getColumnSearchProps("isbn"),
        },
        {
            title: "Rack-Shelf",
            dataIndex: "rackShelf",
            key: "rackShelf",
            width: 200,
            align: "center",
            ...getColumnSearchProps("rackShelf"),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 220,
            sorter: (a, b) => a.title.localeCompare(b.title),
            ...getColumnSearchProps("title"),
        },
        {
            title: "Author",
            dataIndex: "author",
            key: "author",
            width: 220,
            sorter: (a, b) => a.author.localeCompare(b.author),
            ...getColumnSearchProps("author"),
        },
        {
            title: "Publisher",
            dataIndex: "publisher",
            key: "publisher",
            width: 220,
            sorter: (a, b) => a.publisher.localeCompare(b.publisher),
            ...getColumnSearchProps("publisher"),
        },
        {
            title: "ISBN-10",
            dataIndex: "isbN10",
            key: "isbN10",
            width: 220,
            sorter: (a, b) => a.isbN10.localeCompare(b.isbN10),
            ...getColumnSearchProps("isbN10"),
        },
        {
            title: "ISBN-13",
            dataIndex: "isbN13",
            key: "isbN13",
            width: 220,
            sorter: (a, b) => a.isbN13.localeCompare(b.isbN13),
            ...getColumnSearchProps("isbN13"),
        },
        {
            title: "Binding",
            dataIndex: "binding",
            key: "binding",
            width: 220,
            sorter: (a, b) => a.binding.localeCompare(b.binding),
            ...getColumnSearchProps("binding"),
        },
        {
            title: "Pages",
            dataIndex: "pageCount",
            key: "pageCount",
            width: 220,
            sorter: (a, b) => a.pageCount.localeCompare(b.pageCount),
            ...getColumnSearchProps("pageCount"),
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            width: 160,
        },
        {
            title: "Condition",
            dataIndex: "condition",
            key: "condition",
            width: 120,
            ...getColumnSearchProps("condition"),
        },
        {
            title: "UsedCondition",
            dataIndex: "usedCondition",
            key: "usedCondition",
            width: 120,
            ...getColumnSearchProps("usedCondition"),
        },
        {
            title: "Creator",
            dataIndex: "creator",
            key: "creator",
            width: 120,
            ...getColumnSearchProps("creator"),
        },
        {
            title: "Creator1",
            dataIndex: "creator1",
            key: "creator1",
            width: 120,
            ...getColumnSearchProps("creator1"),
        },
        {
            title: "Creator2",
            dataIndex: "creator2",
            key: "creator2",
            width: 120,
            ...getColumnSearchProps("creator2"),
        },
        {
            title: "Creator3",
            dataIndex: "creator3",
            key: "creator3",
            width: 120,
            ...getColumnSearchProps("creator3"),
        },
    ];

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;

        const payload = {
            "startDate": startdate,
            "endDate": enddate,
            "reportID": 12,
            "userIndex": roleUserId,
            "userTimeZone": "string",
            "operatorID": "string",
            "operatorName": "string",
            "qcRework": "string",
            "qcStatus": "string",
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            "search": "string",
            "orderByColumn": "string",
            "orderBy": "string",
            "reportName": "Book Detail Report"
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetReportData, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }

            const books = Array.isArray(result) ? result : [result];
            const formattedData = books[0].bookDetailReport;

            const total = formattedData[0]?.totalRows || 0;
            setTotalRow(total);
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));

            const startUTCTime = getUTCDate();
            setStartTime(startUTCTime?.UTCDate);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <div className="active-page-container">
            <ReportModal
                title={modalTitle}
                modalBody={modalBody}
                Visible={reportModalVisible}
                setVisible={setReportModalVisible}
                queueURL="reportsdownloadqueue"
            />
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>ODBC Report</h4>
                </div>
            </div>
            <div className="advance-filter-container">
                <div className="advance-filter-title-container">
                    <div className="advance-filter-title">
                        <span>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.7245 3.89258V4.35423C14.7245 5.04879 14.7245 5.39607 14.5508 5.68396C14.3771 5.97184 14.0597 6.15052 13.4249 6.50787L11.4754 7.6053C11.0494 7.84506 10.8365 7.96494 10.684 8.09732C10.3665 8.37299 10.171 8.69691 10.0824 9.0942C10.0399 9.28498 10.0399 9.50821 10.0399 9.95468V11.7412C10.0399 13.0191 10.0399 13.658 9.59287 13.9376C9.14585 14.2172 8.51723 13.9714 7.25999 13.48C6.66111 13.2458 6.36167 13.1288 6.19308 12.8915C6.02448 12.6542 6.02448 12.3499 6.02448 11.7412L6.02448 9.95468C6.02448 9.50821 6.02448 9.28498 5.98194 9.0942C5.89336 8.69691 5.69788 8.37299 5.38035 8.09732C5.22787 7.96494 5.01491 7.84506 4.589 7.6053L2.63951 6.50787C2.00471 6.15052 1.68731 5.97184 1.51358 5.68396C1.33984 5.39607 1.33984 5.04879 1.33984 4.35423V3.89258"
                                    stroke="#0009E2"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M14.7245 3.89148C14.7245 3.00351 14.7245 2.55953 14.4305 2.28367C14.1365 2.00781 13.6633 2.00781 12.7168 2.00781H3.34754C2.40111 2.00781 1.92789 2.00781 1.63386 2.28367C1.33984 2.55953 1.33984 3.00351 1.33984 3.89148"
                                    stroke="#009DDB"
                                    strokeWidth="1.2"
                                />
                            </svg></span>Select Date Range</div>
                    <div className="advance-filter-action">
                        <button style={{ display: "none" }} className="active-page-header-button">
                            <span>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        opacity="0.5"
                                        cx="11.5"
                                        cy="11.5"
                                        r="7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.35"
                                    />
                                    <path
                                        d="M15 12H8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        <button
                            style={{ display: "none" }} className="active-page-header-button">
                            <span>
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 25 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12.0311"
                                        cy="11.9999"
                                        r="7.51943"
                                        stroke="#E93548"
                                    />
                                    <path
                                        d="M13.912 10.1192L10.1523 13.8789M10.1523 10.1191L13.912 13.8788"
                                        stroke="#E93548"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                    <div className="date-range-info-label" style={{ paddingBottom: "6px", whiteSpace: "nowrap" }}>
                        Note : Viewing active production data since <strong>{dayjs().subtract(90, "day").format("MM/DD/YYYY")}</strong>
                    </div>
                </div>
                {isVisible && (
                    <div className="advance-filter-body-container">
                        <div className="advance-filter-input-container">
                            <div className="advance-filter-input-width">
                                <div className="field-group">
                                    <DatePicker variant="underlined" placeholder=""
                                        className={`${"book-page-form-input-text"} ${selectedStartDate === null ? "" : "has-text"}`}
                                        suffixIcon={<CalendarIcon />}
                                        value={selectedStartDate}
                                        onChange={handleStartDateChange}
                                        disabledDate={disableFutureDates}
                                        allowClear={false}
                                    />
                                    <label className={startfloatLabel}>
                                        Start Date <span className="form-required">*</span>
                                    </label>
                                    {errors.startdate && (
                                        <span className="error-msg">{errors.startdate}</span>
                                    )}
                                    {errors.datevalidate && (
                                        <span className="error-msg-1">{errors.datevalidate}</span>
                                    )}
                                </div>
                            </div>
                            <div className="advance-filter-input-width">
                                <div className="field-group">
                                    <DatePicker variant="underlined" placeholder=""
                                        className={`${"book-page-form-input-text"} ${selectedEndDate === null ? "" : "has-text"}`}
                                        suffixIcon={<CalendarIcon />}
                                        value={selectedEndDate}
                                        onChange={handleEndDateChange}
                                        disabledDate={disableFutureDates}
                                        allowClear={false}
                                    />
                                    <label className={endfloatLabel}>
                                        End Date <span className="form-required">*</span>
                                    </label>
                                    {errors.enddate && (
                                        <span className="error-msg">{errors.enddate}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="advance-filter-button-container">
                            <button className="cancel-button-gray-outline" onClick={handleCleardate}>Clear</button>
                            <button className="submit-button-gradiant-outline"
                                data-track="reports-active-queue-export-csv"
                                onClick={handleDownloadClick}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Export as CSV'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <div className="active-page-body-container">
                <Modal
                    title={`Manifest Rejection`}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleManifestReject}
                        initialValues={{ bidId: selectedRecord?.bidId }}
                    >
                        <Form.Item
                            name="bidId"
                            label="Bid ID"
                            hidden
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="rejectionType"
                            label="Rejection Type"
                            rules={[
                                { required: true, message: "Please select a rejection type!" },
                            ]}
                        >
                            <Select
                                placeholder="Select a reason type"
                                loading={reasonLoading}
                                options={rejectionOptions.map((option) => ({
                                    value: option.id,
                                    label: option.name,
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="rejectionReason"
                            label="Rejection Reason"
                            rules={[{ required: true, message: "Please provide a reason!" }]}
                        >
                            <TextArea rows={4} />
                        </Form.Item>
                        <Form.Item className="model-action-container">
                            <Space>
                                <button
                                    className="ant-btn ant-btn-default slider-clear-button"
                                    type="button"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="ant-btn ant-btn-primary accept-button"
                                    type="submit"
                                >
                                    Submit
                                </button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};

export default ActiveQueueExport;
